import { reaction } from 'mobx';
import React, { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import b from 'b_';
import { CONTROL_MODE } from '../../constants';
import debug from '../../debug';
import AuditStore, { DIFF_TYPE, REV_TYPE, RULE_REC_TYPE } from '../../stores/AuditStore';
import ConsoleStore from '../../stores/ConsoleStore';
import SourceCountriesStore from '../../stores/Policy/FileRules/SourceCountriesStore';
import { ISOLATION_CHANNELS_OPTIONS } from '../../stores/Policy/MatchCriteriaStore';
import { USER_GROUPS_SOURCES } from '../../stores/Policy/BrowsingRoles/BrowsingRolesStore';
import PolicyMigrationStore from '../../stores/PolicyMigrationStore';
import SnackStore from '../../stores/SnackStore';
import { getGroupOptionDetails } from '../../stores/helpers/userGroups';
import { BrandingOptionView, HelpButtonActionView, HelpButtonView } from '../BrowserBarOptions/BrowserBarFields';
import StatefulBulkEditor, { BULK_EDITOR_STATE } from '../BulkEditor/StatefulBulkEditor';
import { ExitMessageOptionView } from '../ConsoleExitMessage/ExitMessageFields';
import { TextWithTitle } from '../ConsoleFragments';
import ConsoleVirtualList from '../ConsoleFragments/ConsoleVirtualList';
import CustomerContent from '../CustomerContent';
import { Card, Field, FTBody, FTLabel } from '../Fuse';
import FuseWysiwygReadOnly from '../Fuse/FuseWysiwyg/FuseWysiwygReadOnly';
import FuseButton from '../FuseControls/FuseButton';
import FuseReadOnlySelect from '../FuseControls/FuseReadOnlySelect';
import KeyboardShortcuts from '../KeyboardShortcuts';
import { RegistrationTypeLabels } from '../Policy/BrowsingRoles/RegistrationTypeField';
import { EXIT_URL_TEXT } from '../Policy/ContentRules/PageRestrictions';
import PolicyMigrationPage from '../PolicyMigration/PolicyMigrationPage';
import SearchField from '../SearchField/SearchField';
import UDSpinner from '../UD/Fragments/UDSpinner';
import FuseConsoleModal from '../ConsoleModal/FuseConsoleModal';
import UserGroupSourceLabel from '../Policy/UserGroupSourceLabel';
import './ConsoleAuditDetailsModal.css';

const block = b.lock('ConsoleAuditDetailsModal');

const renderValue = (value, ellipsis = true) => {
    return <TextWithTitle
        className={block('ValueField', { ellipsis })}
        text={value}
    />;
};

const toggleRenderer = (trueLabel, falseLabel) => (value) => (value ? trueLabel : falseLabel);
const renderShowHide = toggleRenderer('Display', 'Hide');
const renderEnabledDisabled = toggleRenderer('Enabled', 'Disabled');
const renderBlockAllow = toggleRenderer('Allow', 'Block');
const renderOnOff = toggleRenderer('On', 'Off');
const inverted = (fn) => (value) => fn(!value);
const sortValue = (x, y) => (x + '').localeCompare(y + '');
const selectDiff = (revisionToEntry = (v) => v) => (diff) =>
    <FuseReadOnlySelect
        mode={CONTROL_MODE.AUDIT}
        diff={{
            added: diff.added.map(revisionToEntry),
            removed: diff.removed.map(revisionToEntry)
        }}
    />;
const originalNameRenderer = (name, diff) => diff?.previousRevision?.target?.name ?? name;
/*
* We want to get the value of a property.
* We have a Diff of what changed in the last revision. If that property was changed, the latest value is there.
* If it did not change, then perhaps it changed in the previous revision. If it didn't change in the previous revision -
* the previous revision has a target, that has entire expanded record, not just the diff -> THAT's where we look.
* */
const getLastBoolValue = (key) => (diff) => {
    const val = [
        diff[key],
        diff.previousRevision?.[key],
        diff.previousRevision?.target?.[key]
    ].find((x) => typeof x === 'boolean');
    return val;
};

class ConsoleAuditDetailsModal extends Component {
    static propTypes = {
        audit: AuditStore.PropType.isRequired,
        console: ConsoleStore.PropType.isRequired,
        snack: SnackStore.PropType.isRequired,
    };

    commonInfoFields = [{
        key: 'action',
        title: 'Action',
    }, {
        key: 'target',
        title: 'Changes To',
    }, {
        key: 'time',
        title: 'Date and Time (GMT)',
    }, {
        key: 'actor',
        title: 'Actor',
    }];

    accessCodeFields = [{
        key: 'policyName',
        title: 'Policy Name',
    }, {
        key: 'browsingRoleName',
        title: 'Browsing Role Name',
    }, {
        key: 'code',
        title: 'Access Code',
    }];

    fieldsByType = {
        apiKey: [{
            key: 'apiKey',
            title: 'Key',
        }],
        customerPolicy: [{
            key: 'policyName',
            title: 'Policy Name',
        }],
        CREATE_ACCESS_CODE: this.accessCodeFields,
        EDIT_ACCESS_CODE: this.accessCodeFields,
        DELETE_ACCESS_CODE: this.accessCodeFields,
        [RULE_REC_TYPE.DLP_RULES]: [{
            key: 'name',
            title: 'DLP Rule',
            render: originalNameRenderer
        }],
        [RULE_REC_TYPE.CONTENT_RULES]: [{
            key: 'name',
            title: 'Content Rule',
            render: originalNameRenderer
        }],
        [RULE_REC_TYPE.REDIRECT_RULES]: [{
            key: 'name',
            title: 'Redirect Rule',
            render: originalNameRenderer
        }],
        [RULE_REC_TYPE.EXCLUSION_RULES]: [{
            key: 'name',
            title: 'Exclusion Rule',
            render: originalNameRenderer
        }],
        [RULE_REC_TYPE.FILE_THREAT_RULES]: [{
            key: 'name',
            title: 'File Threat Rule',
            render: originalNameRenderer
        }],
        [REV_TYPE.BROWSING_ROLE]: [{
            key: 'name',
            title: 'Browsing Role',
            render: originalNameRenderer
        }],
        [RULE_REC_TYPE.CONTENT_RULES]: [{
            key: 'name',
            title: 'Content Rule',
            render: originalNameRenderer
        }],
        [RULE_REC_TYPE.CUSTOM_CATEGORIES]: [{
            key: 'category',
            title: 'Custom Category',
            render: (category) => category.name || '--'
        }],
    };

    nameField = {
        key: 'name',
        title: 'Policy Name',
    };

    detailsFetchErrorReactionDisposer = null;

    bulkEditorDiff = (entityName) => (data) => {
        const key = entityName.split(' ').join('-').toLowerCase();
        return <StatefulBulkEditor
            entityName={entityName}
            qa={key}
            mode={{
                type: BULK_EDITOR_STATE.AUDIT,
                added: (data && data.added) || [],
                removed: (data && data.removed) || [],
                removedItemsSearch: this.props.audit.getInputStore(key + '-removed'),
                addedItemsSearch: this.props.audit.getInputStore(key + '-added')
            }}
        />;
    }
    componentDidMount() {
        this.detailsFetchErrorReactionDisposer = reaction(
            () => this.props.audit.detailsStatus.isFailed,
            (isError) => {
                if (isError) {
                    this.props.snack.showError('Failed to load details of audit record');
                }
            });
    }

    componentWillUnmount() {
        if (this.detailsFetchErrorReactionDisposer) {
            this.detailsFetchErrorReactionDisposer();
            this.detailsFetchErrorReactionDisposer = null;
        }
    }

    makeDlpDefaultsCard = (prefix) => ({
        title: 'Changes to "Default Settings" of "Keyboard and Paste Control"',
        fields: [{
            key: `${prefix}.defaults.allInput`,
            title: 'All Input',
            render: inverted(renderEnabledDisabled),
        }, {
            key: `${prefix}.defaults.clipboardPaste`,
            title: 'Clipboard Paste',
            render: inverted(renderEnabledDisabled),
        }, {
            key: `${prefix}.defaults.pasteLimitEnabled`,
            title: 'Paste Limit',
            render: renderEnabledDisabled,
        }, {
            key: `${prefix}.defaults.pasteLimit`,
            title: 'Paste Limit Maximum',
        }],
    });

    ruleCategoriesFields = [{
        key: 'matchAllCategories',
        title: 'Match All URLs',
        render: renderEnabledDisabled,
    }, {
        key: 'tapUserGroups.items',
        title: 'TAP/PPS Groups',
        render: selectDiff((group) => ({
            ...group,
            ...getGroupOptionDetails(group),
        }))
    }, {
        key: 'standardCategories.items',
        title: 'Standard Categories',
        render: selectDiff()
    }, {
        key: 'customerCategories',
        title: 'Custom Categories',
        render: selectDiff(({ category }) => category)
    }]

    ruleMatchCriteriaCard = (ruleRecType) => {
        return {
            title: 'Changes to "Match Criteria"',
            fields: [
                ...(ruleRecType === RULE_REC_TYPE.REDIRECT_RULES
                    ? [{
                        key: 'matchAllUserGroups',
                        title: 'Match All User Groups',
                        render: renderEnabledDisabled,
                    }, {
                        key: 'tapUsersGroups.items',
                        title: 'TAP/PPS Groups',
                        render: selectDiff()
                    }]
                    : [{
                        key: 'matchAllRoles',
                        title: 'Match All Browsing Roles',
                        render: renderEnabledDisabled,
                    }, {
                        key: 'browsingRoles',
                        title: 'Browsing Roles',
                        render: selectDiff()
                    }, {
                        key: 'channels.items',
                        title: 'Isolation Channels',
                        render: selectDiff((ch) => ({
                            name: (ISOLATION_CHANNELS_OPTIONS.find((opt) => opt.value === ch) || {}).label
                        }))
                    }]),
                ...this.ruleCategoriesFields
            ]
        };
    }

    policySummaryCard = {
        title: 'Changes to "Summary"',
        fields: [{
            key: 'name',
            title: 'Name'
        }, {
            key: 'description',
            title: 'Description'
        }, {
            key: 'status',
            title: 'Status',
            render: (v) => v[0].toUpperCase() + v.substring(1).toLowerCase(),
        }]
    }

    makeDlpDomainsCard = (prefix) => ({
        list: {
            key: `${prefix}.domains`,
            addedTitle: 'Added to "Domain Specific Settings" in "Keyboard and Paste Control"',
            removedTitle: 'Removed from "Domain Specific Settings" in "Keyboard and Paste Control"',
            render: (item) => <div className={block('Tr')}>
                <div className={block('Td', { type: 'domain' })} title={item.domain}>
                    {item.domain}
                </div>
                <div className={block('Td', { type: 'allInput' })}>
                    {item.options.allInput ? 'Disabled' : 'Enabled'}
                </div>
                <div className={block('Td', { type: 'paste' })}>
                    {item.options.clipboardPaste
                        ? 'Disabled'
                        : <Fragment>
                            {item.options.pasteLimitEnabled
                                ? `Limited (${item.options.pasteLimit} characters))`
                                : 'Enabled'}
                        </Fragment>}
                </div>
            </div>,
            label: <div className={block('Tr', { header: true })}>
                <div className={block('Td', { type: 'domain' })}>
                    Domain
                </div>
                <div className={block('Td', { type: 'allInput' })}>
                    All input
                </div>
                <div className={block('Td', { type: 'paste' })}>
                    Paste
                </div>
            </div>,
            sort: (x, y) => sortValue(x.domain, y.domain),
        },
    });

    contentFilteringCard = {
        title: 'Changes to "Content Filtering"',
        fields: [{
            key: 'contentFiltering.filterContent',
            title: 'Filter Content',
            render: renderEnabledDisabled,
        }, {
            key: 'contentFiltering.whitelistThreatSim',
            title: 'Allow Proofpoint Training Domains',
            render: renderEnabledDisabled,
        }],
    };

    contentFilteringByCategoryCard = {
        list: {
            addedTitle: 'Added to "Blocked Categories" in "Content Filtering"',
            removedTitle: 'Removed from "Blocked Categories" in "Content Filtering"',
            key: 'contentFiltering.forbiddenCategories',
            label: 'Blocked Categories',
            filterStore: this.props.audit.forbiddenCategoriesFilterStore,
        },
    };

    contentFilteringByUrlWhitelistCard = {
        list: {
            addedTitle: 'Added to "Allowed URLs" in "Content Filtering"',
            removedTitle: 'Removed from "Allowed URLs" in "Content Filtering"',
            key: 'contentFiltering.webFilterList.whitelistRules',
            label: 'Allowed URLs',
            filterStore: this.props.audit.whitelistRulesFilterStore,
        },
    };

    contentFilteringByUrlBlacklistCard = {
        list: {
            addedTitle: 'Added to "Blocked URLs" in "Content Filtering"',
            removedTitle: 'Removed from "Blocked URLs" in "Content Filtering"',
            key: 'contentFiltering.webFilterList.blacklistRules',
            label: 'Blocked URLs',
            filterStore: this.props.audit.blacklistRulesFilterStore,
        },
    };

    cardByDiffType = {
        [DIFF_TYPE[REV_TYPE.USER_GROUP_SOURCE_CHANGED]]: [
            {
                render({ userGroupSource }) {
                    return <Card title="Source Type">
                        <FTBody><UserGroupSourceLabel value={userGroupSource || USER_GROUPS_SOURCES.NONE} /></FTBody>
                    </Card>;
                }
            }
        ],
        [DIFF_TYPE[REV_TYPE.PRIORITY_CHANGED]]: [{
            render: ({ oldOrder, newOrder }) => {
                const priorityContainer = (items, altItems, props) =>
                    <div {...props}>
                        {items.map((name, i) =>
                            <div
                                key={name}
                                className={block('PriorityRow')}
                            >
                                <b>{i + 1}.</b> {name}
                            </div>)}
                    </div>;
                return <Card title='Changes to "Priority"'>
                    <div style={{ display: 'flex' }}>
                        <FTLabel className={block('PriorityHeader', { type: 'original ' })}>Previous Priority</FTLabel>
                        <div className={block('PrioritySpacer')} />
                        <FTLabel className={block('PriorityHeader')}>New Priority</FTLabel>
                    </div>
                    <FTBody className={block('PriorityCardBody')}>
                        {priorityContainer(oldOrder, newOrder, {
                            className: block('PriorityList', { type: 'original' }),
                            'data-qa': 'priority-old-list'
                        })}
                        <div className={block('PrioritySpacer')} />
                        {priorityContainer(newOrder, oldOrder, {
                            className: block('PriorityList', { type: 'new' }),
                            'data-qa': 'priority-new-list'
                        })}
                    </FTBody>
                </Card>;
            },
        }],
        [DIFF_TYPE[RULE_REC_TYPE.REDIRECT_RULES]]: [
            this.policySummaryCard, {
                title: 'Changes to "Options"',
                fields: [{
                    key: 'includeRecentlyScanned',
                    title: 'Include recently scanned',
                    render: renderEnabledDisabled,
                }]
            },
            this.ruleMatchCriteriaCard(RULE_REC_TYPE.REDIRECT_RULES), {
                key: 'senderEmailAddresses',
                render: this.bulkEditorDiff('Email Addresses')
            }, {
                key: 'senderEmailDomains',
                render: this.bulkEditorDiff('Email Domains')
            }
        ],
        [DIFF_TYPE[RULE_REC_TYPE.EXCLUSION_RULES]]: [
            this.policySummaryCard,
            this.ruleMatchCriteriaCard(),
        ],

        [DIFF_TYPE[RULE_REC_TYPE.FILE_THREAT_RULES]]: [
            this.policySummaryCard,
            {
                title: 'Changes to "Match Criteria"',
                fields: [
                    {
                        key: 'matchAllRoles',
                        title: 'Match All Users',
                        render: renderEnabledDisabled,
                    },
                    {
                        key: 'browsingRoles',
                        title: 'Browsing Roles',
                        render: selectDiff()

                    },
                    {
                        key: '_virtual_uploadsDownloads',
                        title: 'File Actions',
                    },
                    {
                        key: 'channels.items',
                        title: 'Isolation Channels',
                        render: selectDiff((ch) => ({
                            name: (ISOLATION_CHANNELS_OPTIONS.find((opt) => opt.value === ch) || {}).label
                        }))
                    },
                    {
                        key: 'matchAllCategories',
                        title: 'Match All URLs',
                        render: renderEnabledDisabled,
                    },
                    {
                        key: 'standardCategories.items',
                        title: 'Standard Categories',
                        render: selectDiff()
                    },
                    {
                        key: 'customerCategories',
                        title: 'Custom Categories',
                        render: selectDiff(({ category }) => category)
                    },
                    {
                        key: 'matchAllFileTypes',
                        title: 'Match All File Types',
                        render: renderEnabledDisabled,
                    },
                    {
                        key: 'matchFileTypes.items',
                        title: 'File Types',
                        render: selectDiff(),
                    },
                    {
                        key: 'matchAllCountries',
                        title: 'Match All Countries',
                        render: renderEnabledDisabled,
                    },
                    {
                        key: 'matchCountries.items',
                        title: 'File Source Countries',
                        render: selectDiff(SourceCountriesStore.getLabelByAbbreviation)
                    },
                    {
                        key: 'matchAllFileSize',
                        title: 'Match All File Sizes',
                        render: renderEnabledDisabled,
                    },
                    {
                        key: 'maximumSizeAllowed',
                        title: 'Max File Size',
                        render: renderValue,
                    },

                ],
            },
            {
                title: 'Changes to "Action"',
                fields: [{
                    key: 'action',
                    title: 'Action',
                    render: (v) => ({
                        SCAN: 'Define based on Additional Conditions',
                        BLOCK: 'Block',
                        ALLOW: 'Allow',
                        PREVIEW: 'Block and allow supported previews'
                    })[v]
                }]
            },
            {
                title: 'Changes to "Additional Conditions"',
                fields: [
                    {
                        key: 'blockMalware',
                        title: 'Malware files and alert',
                        render: renderBlockAllow
                    },
                    {
                        key: 'blockOnTimeout',
                        title: 'Files after a timeout or error',
                        render: renderBlockAllow
                    },
                ]
            }
        ],

        [DIFF_TYPE[RULE_REC_TYPE.CONTENT_RULES]]: [
            this.policySummaryCard,
            this.ruleMatchCriteriaCard(RULE_REC_TYPE.CONTENT_RULES),
            {
                title: 'Changes to "Action"',
                fields: [{
                    title: 'Show page contents',
                    key: 'action',
                    render: (v) => (v === 'ALLOW'
                        ? 'Allow - show the page contents'
                        : 'Block - display a content blocked page')
                }]
            },
            {
                title: 'Changes to "Page Controls"',
                renderCondition: (r) =>
                    // if it was allowed with this revision, or if it's unchanged and it was allowed before
                    r.action === 'ALLOW' || (!r.action && r.previousRevision.target.action === 'ALLOW')
                ,
                fields: [
                    {
                        title: 'Reputation Check',
                        key: 'reputationCheck',
                    },
                    {
                        title: 'Threat Scan',
                        key: 'threatScanning',
                    },
                    {
                        title: 'All Input',
                        key: 'allInput',
                    },
                    {
                        title: 'Clipboard Paste',
                        key: 'clipboardPaste',
                    },
                    {
                        title: 'Paste Limit',
                        key: 'pasteLimit',
                        render: (v) => (v === -1 ? 'Disabled' : renderValue(v))
                    },
                    ...(this.props.console.licenseHasUploadDownload() ? [
                        {
                            title: 'Allow Uploads',
                            key: 'allowUploads',
                        },
                        {
                            title: 'File Upload Threat Scan',
                            key: 'uploadsFileThreatScan',
                            renderCondition: getLastBoolValue('allowUploads')
                        },
                        (this.props.console.showDLPOptions() ? {
                            title: 'File Upload DLP Scan',
                            key: 'uploadsFileDlpScan',
                            renderCondition: getLastBoolValue('allowUploads')
                        } : null),
                        {
                            title: 'Allow Downloads',
                            key: 'allowDownloads',
                        },
                        {
                            title: 'File Download Threat Scan',
                            key: 'downloadsFileThreatScan',
                            renderCondition: getLastBoolValue('allowDownloads')
                        },
                        (this.props.console.showDLPOptions() ? {
                            title: 'File Download DLP Scan',
                            key: 'downloadsFileDlpScan',
                            renderCondition: getLastBoolValue('allowDownloads')
                        } : null),
                    ] : []),
                    {
                        title: 'Safely Preview',
                        key: 'allowFilePreview',
                    },
                    {
                        title: 'Exit URL Isolation',
                        key: 'exitOption',
                        render: (v) => EXIT_URL_TEXT[v]
                    }
                ].filter(Boolean).map((f) => {
                    f.showEmptyValues = true;

                    const originalCondition = f.renderCondition || (() => true);
                    f.renderCondition = (rec) =>
                        // Always show all if we have set the action to ALLOW now, OR show them only if they changed.
                        originalCondition(rec) && (typeof rec[f.key] !== 'undefined' || rec.action === 'ALLOW');
                    f.render = f.render || ((_, diff) => renderEnabledDisabled(getLastBoolValue(f.key)(diff)));
                    return f;
                })
            }
        ],

        [DIFF_TYPE[RULE_REC_TYPE.DLP_RULES]]: [
            this.policySummaryCard,
            {
                title: 'Changes to "Match Criteria"',
                fields: [
                    {
                        key: 'matchAllRoles',
                        title: 'Match All Users',
                        render: renderEnabledDisabled,
                    },
                    {
                        key: 'browsingRoles',
                        title: 'Browsing Roles',
                        render: selectDiff()
                    },
                    {
                        key: '_virtual_uploadsDownloads',
                        title: 'File Actions'
                    },
                    {
                        key: 'channels.items',
                        title: 'Isolation Channels',
                        render: selectDiff((ch) => ({
                            name: (ISOLATION_CHANNELS_OPTIONS.find((opt) => opt.value === ch) || {}).label
                        }))
                    },
                    {
                        key: 'matchAllCategories',
                        title: 'Match All URLs',
                        render: renderEnabledDisabled,
                    },
                    {
                        key: 'standardCategories.items',
                        title: 'Standard Categories',
                        render: selectDiff()
                    },
                    {
                        key: 'customerCategories',
                        title: 'Custom Categories',
                        render: selectDiff(({ category }) => category)
                    },
                    {
                        key: 'matchAllFileTypes',
                        title: 'Match All File Types',
                        render: renderEnabledDisabled,
                    },
                    {
                        key: 'matchFileTypes.items',
                        title: 'File Types',
                        render: selectDiff(),
                    },
                    {
                        key: 'matchAllCountries',
                        title: 'Match All Countries',
                        render: renderEnabledDisabled,
                    },
                    {
                        key: 'matchCountries.items',
                        title: 'File Source Countries',
                        render: selectDiff(SourceCountriesStore.getLabelByAbbreviation)
                    },
                    {
                        key: 'matchAllFileSize',
                        title: 'Match All File Sizes',
                        render: renderEnabledDisabled,
                    },
                    {
                        key: 'maximumSizeAllowed',
                        title: 'Max File Size',
                        render: renderValue,
                    },
                ],
            },
            {
                title: 'Changes to "Action"',
                fields: [{
                    title: 'Action',
                    key: 'action',
                    render: (v) => ({
                        SCAN: 'Define based on Additional Conditions',
                        BLOCK: 'Block',
                        ALLOW: 'Allow',
                    })[v]
                }]
            },
            {
                title: 'Changes to "Additional Conditions"',
                fields: [
                    {
                        key: 'blockOnTimeout',
                        title: 'Files after a timeout or error',
                        render: renderBlockAllow
                    },
                    {
                        key: 'blockOnDetection',
                        title: 'Matching Detector Sets',
                        render: renderBlockAllow
                    },
                    {
                        key: 'detectorSets.items',
                        title: 'Detector Sets',
                        render: selectDiff(),
                    },
                    {
                        key: 'blockUnsupportedFiles',
                        title: 'Unsupported & encrypted file types',
                        render: renderBlockAllow
                    },
                ]
            }
        ],

        [DIFF_TYPE[REV_TYPE.BROWSING_ROLE]]: [
            this.policySummaryCard,
            {
                title: 'Changes to "Assigned Users"',
                fields: [{
                    key: 'effectsUserGroups.items',
                    title: 'User Groups',
                    render: selectDiff()
                }]
            }, {
                key: 'effectsEmails',
                render: this.bulkEditorDiff('Email Addresses')
            }, {
                title: 'Changes to "Browser/Email Isolation Options"',
                fields: [{
                    key: 'browserRegistrationEnabled',
                    title: 'Registration status',
                    render: renderEnabledDisabled,
                }, {
                    key: 'anonymousUsage',
                    title: 'Allow Anonymous Usage',
                    render: renderEnabledDisabled,
                }, {
                    key: 'browserAuthenticationMethod',
                    title: 'Registration type',
                    render: (v) => RegistrationTypeLabels[v],
                }, {
                    key: 'allowResearchMode',
                    title: 'Allow Research Mode',
                    render: renderEnabledDisabled
                }]
            }
        ],

        URLIsolationPolicy: [{
            title: 'Changes to "Policy Definition"',
            fields: [this.nameField, {
                key: 'enabled',
                title: 'Redirect Status',
                render: renderEnabledDisabled,
            }, {
                key: 'priority',
                title: 'Priority Order',
                render: () => 'Changed',
            }],
        }, {
            list: {
                addedTitle: 'Added to "Groups to Redirect"',
                removedTitle: 'Removed from "Groups to Redirect"',
                key: 'userGroups',
                label: 'Groups to Redirect',
                filterStore: this.props.audit.userGroupsFilterStore,
            },
        }, {
            list: {
                addedTitle: 'Added to "Categories to Redirect" in "Redirect Options"',
                removedTitle: 'Removed from "Categories to Redirect" in "Redirect Options"',
                key: 'isolatedCategories',
                label: 'Categories to Redirect',
                filterStore: this.props.audit.isolatedCategoriesFilterStore,
            },
        }, {
            list: {
                addedTitle: 'Added to "Redirect Exceptions" in "Redirect Options"',
                removedTitle: 'Removed from "Redirect Exceptions" in "Redirect Options"',
                key: 'neverIsolate',
                label: 'Never Isolate URLs',
                filterStore: this.props.audit.isolatedCategoriesFilterStore,
            },
        }, {
            title: 'Changes to "Security Options"',
            fields: [{
                key: 'allowExitIsolationFromSafeState',
                title: 'Allow users to “Exit Isolation”',
                render: renderEnabledDisabled,
            }, {
                key: 'displayExitWarning',
                title: 'Display exit confirmation',
                render: renderEnabledDisabled,
            }, {
                key: 'exitWarningText',
                title: 'Confirmation message',
                render: renderValue,
            }],
        },
        this.makeDlpDefaultsCard('dlp'),
        this.makeDlpDomainsCard('dlp'),
        this.contentFilteringCard,
        this.contentFilteringByCategoryCard,
        this.contentFilteringByUrlWhitelistCard,
        this.contentFilteringByUrlBlacklistCard,
        ...(this.props.console.isConsoleAdmin ? [{
            title: 'Changes to "Proofpoint Admin Options"',
            fields: [{
                key: 'alwaysRedirectIgnoringScan',
                title: 'Always redirect URL regardless of a recently safe scan',
                render: renderEnabledDisabled,
            }, {
                key: 'scanTimeout',
                title: 'Specify a timeout while waiting for a full scan',
                render: (value) => `${value} seconds`,
            }, {
                key: 'neverIsolateAfterRedirect',
                title: 'Process Never Isolate Entries After Redirect',
                render: renderEnabledDisabled,
            }],
        }] : [])],

        BrowserIsolationPolicy: [{
            title: 'Changes to "Policy Definition"',
            fields: [this.nameField],
        }, {
            title: 'Changes to "Menu Items" in "User Interface Options"',
            fields: [{
                key: 'printEnabled',
                title: 'Print',
                render: renderShowHide,
            }, {
                key: 'clearCookiesEnabled',
                title: 'Clear Site Cookies',
                render: renderShowHide,
            }, {
                key: 'feedbackEnabled',
                title: 'Feedback',
                render: renderShowHide,
            }, {
                key: 'faqEnabled',
                title: 'FAQ & Help',
                render: renderShowHide,
            }],
        }, {
            title: 'Changes to "Settings Page" in "User Interface Options"',
            fields: [{
                key: 'settingsResearchMode',
                title: 'Research Mode',
                render: renderShowHide,
            }],
        }, {
            title: 'Changes to "Browser Bar" in "User Interface Options"',
            fields: [{
                key: 'adBlockSwitchEnabled',
                title: 'Ad Blocker',
                render: renderShowHide,
            }, {
                key: 'blockAdsByDefault',
                title: 'Block Ads by Default',
                render: renderOnOff,
            }],
        }, {
            title: 'Changes to "Security Options',
            fields: [{
                key: 'checkUnsafe',
                title: 'Reputation Check',
                render: renderEnabledDisabled,
            }, {
                key: 'tsaScan',
                title: 'Threat Scan',
                render: renderEnabledDisabled,
            }],
        },
        this.makeDlpDefaultsCard('dataLossPrevention'),
        this.makeDlpDomainsCard('dataLossPrevention'),
        this.contentFilteringCard,
        this.contentFilteringByCategoryCard,
        this.contentFilteringByUrlWhitelistCard,
        this.contentFilteringByUrlBlacklistCard],
        User: [{
            title: 'Changes',
            fields: [{
                key: 'userEmail',
                title: 'Email',
            }, {
                key: 'firstName',
                title: 'First Name',
            }, {
                key: 'lastName',
                title: 'Last Name',
            }, {
                key: 'roleAccess',
                title: 'Role',
            }],
        }],
        CustomBlockPage: [{
            title: 'Changes',
            fields: [{
                key: 'message',
                title: 'Message',
                render: (value) => renderValue(value, false),
            }, {
                key: 'logo',
                title: 'Logo',
                render: () => 'logo changed',
            }, {
                key: 'headline',
                title: 'Headline',
            }, {
                key: 'headlineIcon',
                title: 'Headline Icon',
                render: (value) => `${renderShowHide(value)} icon`,
            }, {
                key: 'background',
                title: 'Background',
                render: (value) => `${renderShowHide(value)} image`,
            }, {
                key: 'enabled',
                title: 'Block Page Contents',
                render: (value) => (value ? 'custom block page' : 'default block page'),
            }, {
                key: 'content',
                title: 'Message',
                render: (value) => (Array.isArray(value)
                    ? <FuseWysiwygReadOnly content={value} />
                    : 'settings changed'),
            }],
        }],
        RegistrationNotice: [{
            title: 'Changes',
            fields: [{
                key: 'message',
                title: 'Message',
                render: (value) => renderValue(value, false),
            }, {
                key: 'headline',
                title: 'Headline',
                render: (value) => renderValue(value, false),
            }, {
                key: 'enabled',
                title: 'Registration Notice',
                render: (value) => `Registration Notice ${value ? 'Enabled' : 'Disables'}`,
            }, {
                key: 'content',
                title: 'Message',
                render: (value) => (Array.isArray(value)
                    ? <FuseWysiwygReadOnly content={value} />
                    : 'settings changed'),
            }],
        }],
        AccessCodes: [{
            title: 'Changes to "Access Code"',
            fields: [{
                key: 'neverExpire',
                title: 'Never Expire',
                render: renderOnOff,
            }, {
                key: 'expirationDate',
                title: 'Code Valid Through',
            }, {
                key: 'unlimitedActivations',
                title: 'Unlimited Activations',
                render: renderOnOff,
            }, {
                key: 'activationsAllowed',
                title: 'Activations Limit',
            }],
        }],
        AccessCodesRedesigned: [{
            title: 'Changes to "Access Code"',
            fields: [{
                key: 'neverExpire',
                title: 'Expiration Date',
                render: (value) => renderEnabledDisabled(!value),
            }, {
                key: 'expirationDate',
                title: 'Code Valid Through',
            }, {
                key: 'unlimitedActivations',
                title: 'Limited Activations',
                render: (value) => renderEnabledDisabled(!value),
            }, {
                key: 'activationsAllowed',
                title: 'Activations Limit',
            }],
        }],
        MetaConfig: [{
            title: 'Changes',
            fields: [{
                key: 'metaEnabled',
                title: 'SAML Status',
                render: renderEnabledDisabled,
            }, {
                key: 'externalIdpConfig',
                title: 'Authentication Type',
                render: (value) => (value ? 'Proofpoint Meta' : 'Configure an IdP'),
            }, {
                key: 'metaApiKeyId',
                title: 'SCIM API Key',
                render: (value) => (value && value !== '—' ? 'Key generated' : 'Key deleted'),
            }, {
                key: 'externalIdpName',
                title: 'IdP Configuration',
            }, {
                key: 'authn_context_class',
                title: 'Authentication Context Class',
            }, {
                key: 'issuer',
                title: 'Identity Provider Issuer',
            }, {
                key: 'certificate',
                title: 'X.509 Certificate',
                render: () => 'Configuration changed',
            }, {
                key: 'sso_url',
                title: 'Identity Provider SSO URL',
            }, {
                key: 'metaIdpId',
                title: 'Identity Provider Configuration',
                render: (value) => (value === null || value === '—' ? 'removed' : 'created'),
            }, {
                key: 'assume_ownership',
                title: 'IdP Assume Ownership',
                render: renderEnabledDisabled,
            }, {
                key: 'jit_enabled',
                title: 'Just In Time (JIT)',
                render: renderEnabledDisabled,
            }],
        }],
        CustomerCategory: [{
            title: 'Changes to "Custom Categories"',
            fields: [{
                key: 'name',
                title: 'Name',
            }, {
                key: 'description',
                title: 'Description',
                render: (value) => (value && value.trim() ? value : '-')
            }],
        }, {
            key: 'ipAndUrlPatterns',
            render: this.bulkEditorDiff('IP Addresses and URLs')
        }],
        MetaAuthority: [{
            title: 'User Groups',
            list: {
                addedTitle: 'Added Full Access User Groups',
                removedTitle: 'Removed Full Access User Groups',
                key: 'fullAccessUserGroups',
                label: 'Group name',
            },
        }, {
            title: 'User Groups',
            list: {
                addedTitle: 'Added Read Only User Groups',
                removedTitle: 'Removed Read Only User Groups',
                key: 'readOnlyUserGroups',
                label: 'Group name',
            },
        }],
        RegistrationOptions: [{
            title: 'Changes',
            fields: [{
                key: 'emailRegistration',
                title: 'Registration selected',
            }],
        }, {
            list: {
                addedTitle: 'Added to "Email Registration"',
                removedTitle: 'Removed from "Email Registration"',
                key: 'whitelist',
                label: 'Allowed Email Domains',
                filterStore: this.props.audit.whiteListFilterStore,
            },
        }, {
            list: {
                addedTitle: 'Added to "IP Address / Subnetwork"',
                removedTitle: 'Removed from "IP Address / Subnetwork"',
                key: 'ipAddress',
                label: 'IP Range Registration',
                filterStore: this.props.audit.ipAddressFilterStore,
            },
        }],
        BrowserBar: [{
            title: 'Changes',
            fields: [{
                key: 'brandingOption',
                title: 'Branding Option',
                render: (value) => BrandingOptionView[value],
            }, {
                key: 'customBrandingText',
                title: 'Custom Branding',
            }, {
                key: 'externalLink',
                title: 'External Link',
            }, {
                key: 'helpButton',
                title: 'Help Link Option',
                render: (value) => HelpButtonView[value],
            }, {
                key: 'helpButtonAction',
                title: 'Click Option',
                render: (value) => HelpButtonActionView[value],
            }, {
                key: 'popupMessage',
                title: 'Popup Message',
                render: (value) => <CustomerContent html={value} />,
            }, {
                key: 'popupContent',
                title: 'Popup Message',
                render: (value) => <FuseWysiwygReadOnly content={value} />,
            }],
        }],
        ExitMessage: [{
            title: 'Changes',
            fields: [{
                key: 'option',
                title: 'Exit Message Option',
                render: (value) => ExitMessageOptionView[value],
            }, {
                key: 'text',
                title: 'Exit Message',
            }, {
                key: 'content',
                title: 'Exit Message',
                render: (value) => <FuseWysiwygReadOnly content={value} />,
            }],
        }],
        PolicyMigration: [{
            title: 'Changes to "New Policies Migration"',
            fields: [{
                key: 'policyMigrationStep',
                title: 'Migration Step',
                render: (value) => PolicyMigrationPage.STEP_COPY[PolicyMigrationStore.stepNumberFromString(value)]
            }],
        }]
    };

    renderList = (items, list, isAdded) => {
        if (!items || !items.length) {
            return null;
        }

        const title = isAdded ? list.addedTitle : list.removedTitle;
        const renderSearch = !!list.filterStore && items.length > 8;
        const filteredList = items.filter((x) => {
            const query = renderSearch ? list.filterStore.value.trim().toLowerCase() : '';
            return !query || JSON.stringify(x).toLowerCase().indexOf(query) !== -1;
        }).sort(list.sort || sortValue);

        return <Card
            key={title}
            title={title}
            titleAction={renderSearch ? <SearchField
                className={block('ListFilter')}
                inputStore={list.filterStore}
                placeholder="Search in list"
            /> : null}
        >
            <ConsoleVirtualList
                className={block('List')}
                titleClassName={block('ListTitle')}
                title={list.label}
                itemCount={filteredList.length}
                itemSize={48}
                height={Math.min(384, 48 * filteredList.length)}
            >{({ index }) => (list.render || renderValue)(filteredList[index])}</ConsoleVirtualList>
        </Card>;
    };

    renderDiff(diff) {
        debug.log('renderDiff', diff);

        if (!diff) {
            return null;
        }

        return <Fragment>
            {this.cardByDiffType[diff.type].flatMap((card) => {
                if (card.renderCondition && !card.renderCondition(diff)) {
                    return null;
                }
                if (card.render) {
                    const data = card.key && diff[card.key] !== 'undefined' ? diff[card.key] : diff;
                    return card.render(data);
                }
                // renderFields should probably take the record here, not the diff.
                // At least that's how it's called in other places. E.g. record first, and then diff third param.
                // However, Im too scared to change it because I don't want to go through all old audit logs
                const fields = this.renderFields(diff, card.fields || [], diff);

                if (fields.length > 0) {
                    return <Card
                        key={card.title}
                        title={card.title}
                    >{fields}</Card>;
                }

                if (card.list && diff[card.list.key]) {
                    const { added, removed } = diff[card.list.key];
                    return [
                        this.renderList(removed, card.list, false),
                        this.renderList(added, card.list, true),
                    ];
                }
                return null;
            }).filter(Boolean)}
        </Fragment>;
    }

    checkEmptyReturnDash(value) {
        return typeof value === 'boolean' || typeof value === 'number' ? value : value || '—';
    }

    renderFields(auditRecordOrDiff, fields, diff, showEmptyValues) {
        return (fields || [])
            .map((field) => {
                const value = auditRecordOrDiff[field.key];
                if ((!showEmptyValues && !field.showEmptyValues && value === undefined) ||
                    field.renderCondition?.(auditRecordOrDiff) === false) {
                    return null;
                }
                return <Field
                    key={field.key}
                    label={field.title}
                >{(field.render || renderValue)(this.checkEmptyReturnDash(value), diff)}</Field>;
            })
            .filter(Boolean);
    }

    renderBody(auditRecord) {
        const { audit } = this.props;
        const diff = audit.getDiff(auditRecord);

        return this.props.audit.detailsStatus.isLoading ? <Card title="Audit Information">
            <UDSpinner style={{ margin: '0 auto' }} type="small" />
        </Card> : <Fragment>
            <Card title="Audit Information">
                {this.renderFields(auditRecord, this.commonInfoFields, diff, true)}
                {this.renderFields(auditRecord, this.fieldsByType[auditRecord.type], diff, true)}
            </Card>
            {this.renderDiff(diff)}
        </Fragment>;
    }

    render() {
        const { audit } = this.props;
        const auditRecord = audit.getSelectedRecord();
        const isFirst = audit.filteredList[0] === auditRecord;
        const isLast = audit.filteredList[audit.filteredList.length - 1] === auditRecord;
        return <FuseConsoleModal
            className={block()}
            isOpen={audit.modal.isOpen()}
            onClose={audit.modal.close}
            title="Recorded Data"
            contentClassName={block('Content')}
            headerActions={<Fragment>
                <FuseButton
                    icon="keyboard_arrow_left"
                    onClick={() => audit.showPrev(auditRecord)}
                    style={{ marginLeft: 20 }}
                    disabled={isFirst}
                    color="blue"
                >Prev</FuseButton>
                <FuseButton
                    iconRight="keyboard_arrow_right"
                    onClick={() => audit.showNext(auditRecord)}
                    style={{ marginLeft: 20 }}
                    disabled={isLast}
                    color="blue"
                >Next</FuseButton>
            </Fragment>}
        >
            {auditRecord ? <Fragment>
                {this.renderBody(auditRecord)}
                <KeyboardShortcuts
                    esc={audit.modal.close}
                    left={() => audit.showPrev(auditRecord)}
                    up={() => audit.showPrev(auditRecord)}
                    right={() => audit.showNext(auditRecord)}
                    down={() => audit.showNext(auditRecord)}
                />
            </Fragment> : null}
        </FuseConsoleModal>;
    }
}

export default inject('audit', 'snack', 'console')(observer(ConsoleAuditDetailsModal));
